import React, { createContext, useContext, useRef, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CheckCircle, X } from 'lucide-react';
import { BASE_URL } from "../config/apiconfig";
import { loadRazorpay } from "../../utils/loadRazorpay.jsx";
import { useNavigate } from "react-router-dom";

const PaymentGatewayContext = createContext(null);
export const usePaymentGateway = () => useContext(PaymentGatewayContext);

export const PaymentGatewayProvider = ({ children }) => {
  const navigate = useNavigate(); // ✅ works because this is now inside <BrowserRouter> in App.js

  const getAuthFunctions = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.dispatchEvent(new Event("auth-change"));
      };
      return { user, logout };
    } catch (error) {
      return {
        user: null,
        logout: () => {
          localStorage.clear();
          window.location.href = "/signin";
        }
      };
    }
  };

  const activeInstanceRef = useRef(null);
  const isProcessingRef = useRef(false);
  const pendingOrdersRef = useRef(new Map());
  const modalSessionActiveRef = useRef(false);
  const currentCallbacksRef = useRef(null);

  const safeNavigate = useCallback((path, options = {}) => {
    try {
      navigate(path, options);
    } catch (error) {
      if (options.replace) {
        window.location.replace(path);
      } else {
        window.location.href = path;
      }
    }
  }, [navigate]);

  const cleanup = useCallback(() => {
    if (activeInstanceRef.current) {
      try {
        if (activeInstanceRef.current.off) {
          activeInstanceRef.current.off("payment.failed");
        }
        if (activeInstanceRef.current.close) {
          activeInstanceRef.current.close();
        }
      } catch (error) {
      }
      activeInstanceRef.current = null;
    }
    isProcessingRef.current = false;
    modalSessionActiveRef.current = false;
    currentCallbacksRef.current = null;
  }, []);

  const isPaymentActive = useCallback(() => {
    return isProcessingRef.current || activeInstanceRef.current !== null;
  }, []);

  const forceCleanup = useCallback(() => {
    cleanup();
    pendingOrdersRef.current.clear();
  }, [cleanup]);

  const checkUserStatus = useCallback(async (userId) => {
    const { logout } = getAuthFunctions();

    try {

      const response = await axios.get(`${BASE_URL}/user_status/${userId}`, {
        timeout: 10000,
        headers: { 'Content-Type': 'application/json' }
      });


      if (response.data.status === 200 && response.data.data) {
        const { account_status } = response.data.data;

        if (account_status === 'blocked') {
          toast.error('Your account has been blocked. Please contact support for assistance.', {
            duration: 6000,
            icon: <X size={16} className="text-red-600" />,
          });
          logout();
          safeNavigate('/signin', { replace: true });
          return { success: false, blocked: true, message: 'Account is blocked' };

        } else if (account_status === 'active') {
          return { success: true, active: true, message: 'Account is active' };

        } else {
          toast.error('Unable to verify account status. Please try again.', { duration: 5000 });
          return { success: false, message: `Unknown account status: ${account_status}` };
        }

      } else {
        toast.error('Unable to verify account status. Please try again.', { duration: 5000 });
        return { success: false, message: 'Invalid response format' };
      }

    } catch (error) {

      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error('Request timeout. Please check your connection and try again.', { duration: 5000 });
        return { success: false, message: 'Request timeout' };

      } else if (error.response?.status === 404) {
        toast.warning('Unable to verify account status. Proceeding with payment.', { duration: 4000 });
        return { success: true, message: 'User not found, allowing payment' };

      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', { duration: 5000 });
        logout();
        safeNavigate('/signin', { replace: true });
        return { success: false, unauthorized: true, message: 'Unauthorized access' };

      } else {
        toast.warning('Unable to verify account status. Proceeding with payment.', { duration: 4000 });
        return { success: true, message: 'Status check failed, allowing payment' };
      }
    }
  }, [safeNavigate]);

  const openRazorpay = useCallback(async (amount, user, callbacks = {}) => {
    if (isProcessingRef.current) {
      throw new Error("Payment already in progress");
    }

    cleanup();
    const loaded = await loadRazorpay();

  if (!loaded) {
    cleanup();
    throw new Error("Unable to load Razorpay SDK");
  }


    isProcessingRef.current = true;
    modalSessionActiveRef.current = true;
    currentCallbacksRef.current = callbacks;

    try {
      if (user?.id) {
        const statusCheck = await checkUserStatus(user.id);

        if (!statusCheck.success) {
          cleanup();
          if (statusCheck.blocked) {
            throw new Error("Account is blocked. Payment cannot be processed.");
          } else if (statusCheck.unauthorized) {
            throw new Error("Session expired. Please login again.");
          } else {
            throw new Error(statusCheck.message || "Unable to verify account status");
          }
        }

      } else {
        toast.warning('Unable to verify user status. Proceeding with payment.', { duration: 4000 });
      }

      const orderKey = `${amount}_${user?.id || 'guest'}_${Date.now()}`;

      if (pendingOrdersRef.current.has(orderKey)) {
        throw new Error("Duplicate order creation attempt");
      }

      pendingOrdersRef.current.set(orderKey, true);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      // Prefer sending the cart so the SERVER computes the authoritative amount
      // (prices/bulk/promo/tax from the catalogue). Falls back to the raw amount
      // only if the cart wasn't provided.
      const orderPayload = (callbacks?.products && callbacks.products.length)
        ? { products: callbacks.products, promo_code: callbacks.promo_code || '' }
        : { amount };

      let res;
      try {
        res = await axios.post(
          `${BASE_URL}/order`,
          orderPayload,
          {
            signal: controller.signal,
            timeout: 15000,
            headers: { 'Content-Type': 'application/json' }
          }
        );
      } finally {
        clearTimeout(timeoutId);
        pendingOrdersRef.current.delete(orderKey);
      }

      if (res.data.status !== "success") {
        throw new Error(res.data.message || "Failed to create payment order");
      }

      const { order_id, amount: orderAmount, currency } = res.data;

      return new Promise((resolve, reject) => {
        if (!isProcessingRef.current) {
          reject({
            success: false,
            cancelled: true,
            reason: "payment_cancelled_before_modal",
            message: "Payment was cancelled"
          });
          return;
        }

        const options = {
          key: "rzp_test_SSeEqDbAczl7iq",
          amount: orderAmount,
          currency,
          name: "Manaprint",
          description: "Order Payment",
          order_id,
          prefill: {
            name: user?.name || user?.first_name || "",
            email: user?.email || "",
            contact: user?.phone || "",
          },
          theme: { color: "#008080" },
          modal: {
            escape: true,
            ondismiss: function () {

              if (modalSessionActiveRef.current) {
                toast.error("Payment cancelled. You can retry anytime.", {
                  duration: 4000,
                  icon: <X size={16} className="text-red-600" />,
                });
              }

              if (currentCallbacksRef.current?.onModalClose) {
                try {
                  currentCallbacksRef.current.onModalClose();
                } catch (error) {
                }
              }

              cleanup();

              reject({
                success: false,
                cancelled: true,
                reason: "modal_dismissed",
                message: "Payment cancelled by user"
              });
            }
          },
          method: {
            card: true,
            netbanking: true,
            upi: true,
            wallet: true,
            emi: false,
          },
          handler: function (response) {

            if (!modalSessionActiveRef.current) {
              return;
            }

            if (!currentCallbacksRef.current) {
              return;
            }

            toast.success("Payment Successful!", {
              icon: <CheckCircle size={16} className="text-green-600" />
            });

            modalSessionActiveRef.current = false;

            if (currentCallbacksRef.current.onSuccess) {
              try {
                requestAnimationFrame(() => {
                  currentCallbacksRef.current.onSuccess(response);
                });
              } catch (error) {
              }
            }

            resolve({
              success: true,
              paymentResponse: response,
              orderId: order_id
            });
          }
        };

        try {
          const rzp = new window.Razorpay(options);
          activeInstanceRef.current = rzp;

          const failureHandler = function (response) {

            if (modalSessionActiveRef.current) {
              toast.error("Payment failed. Please try again.", {
                duration: 5000,
                icon: <X size={16} className="text-red-600" />,
              });
            }

            if (currentCallbacksRef.current?.onFailure) {
              try {
                currentCallbacksRef.current.onFailure(response);
              } catch (error) {
              }
            }

          };

          rzp.on("payment.failed", failureHandler);
          rzp.open();

        } catch (error) {
          cleanup();
          reject({
            success: false,
            reason: "razorpay_instance_error",
            message: error.message || "Failed to create payment interface"
          });
        }
      });

    } catch (error) {
      cleanup();

      if (error.name === 'AbortError') {
        toast.error("Payment request timed out. Please try again.");
        throw new Error("Payment request timed out");
      } else if (error.message === "Payment already in progress") {
        toast.warning("Payment is already in progress. Please wait.");
        throw error;
      } else if (error.message === "Duplicate order creation attempt") {
        toast.warning("Order is being created. Please wait.");
        throw error;
      } else if (error.message.includes("Account is blocked")) {
        throw error;
      } else if (error.message.includes("Session expired")) {
        throw error;
      } else {
        toast.error("Payment initiation failed. Please try again.");
        throw error;
      }
    }
  }, [cleanup, checkUserStatus]);

  const createPaymentOrder = useCallback(async (amount, user, callbacks) => {
    return openRazorpay(amount, user, callbacks);
  }, [openRazorpay]);

  const contextValue = {
    openRazorpay,
    createPaymentOrder,
    forceCleanup,
    isPaymentActive,
    checkUserStatus
  };

  return (
    <PaymentGatewayContext.Provider value={contextValue}>
      {children}
    </PaymentGatewayContext.Provider>
  );
};